import React, { useRef, useState } from 'react';
import { Sparkles, Send } from 'lucide-react';
import { askCrmAssistant } from '../services/crmService';

const suggestions = [
  'Invite inactive players back. Friendly tone, no discount.',
  'Encourage players to book a weekday game. No special offer.',
  'Help me announce a new court. Ask me for the opening details.',
  'Help me write an invitation to an event. Ask me for the details.',
];

export default function CrmCampaignAssistant({ campaign, audienceLabel, venueLabel, onApply, disabled, initialPrompt = '' }) {
  const [message, setMessage] = useState(initialPrompt);
  const [history, setHistory] = useState([]);
  const [proposal, setProposal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);

  async function generate(instruction = message) {
    if (!instruction.trim() || pending.current || disabled) return;
    pending.current = true;
    setBusy(true); setError('');
    const snapshot = { ...campaign };
    const content = document.createElement('template');
    content.innerHTML = campaign.htmlBody;
    const blocks = Array.from(content.content.querySelectorAll('p')).filter(p => !p.querySelector('a')).map(p => p.textContent);
    const paragraphs = blocks.length ? blocks : (content.content.textContent || '').split(/\n+/).filter(Boolean);
    const editorDraft = campaign.subject && campaign.name && paragraphs.length
      ? { name: campaign.name, subject: campaign.subject,
        paragraphs: paragraphs.slice(0, 12).map(p => p.slice(0, 2000)),
        buttonText: content.content.querySelector('a')?.textContent?.slice(0, 80) || null } : null;
    const currentDraft = proposal && !changed ? proposal.draft : editorDraft;
    try {
      const result = await askCrmAssistant({ message: instruction.trim(), segment: campaign.segment,
        venueId: campaign.venueId || null, currentDraft, history: history.slice(-10) });
      setHistory(items => [...items, { role: 'user', content: instruction.trim() },
        { role: 'assistant', content: result.reply }].slice(-12));
      setProposal(result.draft ? { ...result, snapshot } : null);
      setMessage('');
    } catch (err) {
      setError(err.response?.data?.message || 'The assistant could not respond. Your email is unchanged. Please retry.');
    } finally { pending.current = false; setBusy(false); }
  }

  const changed = proposal && Object.keys(proposal.snapshot).some(key => proposal.snapshot[key] !== campaign[key]);
  return <section className="card space-y-4 border border-teal-100 p-5">
    <div><h3 className="flex items-center gap-2 font-semibold"><Sparkles size={18} className="text-[#0B7F83]"/>Campaign assistant</h3>
      <p className="mt-1 text-xs text-[#64748B]">Draft ideas for {venueLabel} · {audienceLabel}. Review facts before sending.</p></div>
    {!history.length && <div className="flex flex-wrap gap-2">{suggestions.map(text => <button type="button" key={text}
      disabled={busy || disabled} onClick={() => setMessage(text)} className="rounded-xl bg-[#E8F7F5] px-3 py-2 text-left text-xs text-[#397D75] disabled:opacity-50">{text}</button>)}</div>}
    <div aria-live="polite" className="max-h-72 space-y-3 overflow-y-auto">{history.map((turn, index) => <div key={index}
      className={`rounded-xl p-3 text-sm whitespace-pre-wrap ${turn.role === 'user' ? 'bg-slate-100' : 'bg-teal-50'}`}>
      <p className="mb-1 text-xs font-semibold">{turn.role === 'user' ? 'You' : 'Assistant'}</p>{turn.content}</div>)}</div>
    {proposal && <div className="rounded-xl border border-teal-200 p-4 text-sm">
      <p className="text-xs font-semibold text-[#64748B]">Suggested draft</p><p className="mt-1 font-semibold">{proposal.draft.subject}</p>
      {proposal.draft.paragraphs.map((text, index) => <p key={index} className="mt-2 whitespace-pre-wrap">{text}</p>)}
      {proposal.draft.buttonText && <p className="mt-2 font-semibold text-[#0B7F83]">{proposal.draft.buttonText}</p>}
      {changed && <p className="mt-3 text-xs text-amber-800">Your email or audience changed. Generate a fresh draft to preserve those edits.</p>}
      <button type="button" disabled={changed || busy || disabled} className="btn-primary mt-3" onClick={() => {
        onApply({ name: proposal.draft.name, subject: proposal.draft.subject, htmlBody: proposal.htmlBody });
        setProposal(null);
      }}>Use this draft</button></div>}
    <label htmlFor="crm-assistant-message" className="block text-sm font-medium">What would you like to write or change?</label>
    <textarea id="crm-assistant-message" rows={3} maxLength={2000} className="input-field h-auto py-3" value={message}
      onChange={event => setMessage(event.target.value)} placeholder="Describe the goal, offer, dates, tone or language…" disabled={busy || disabled}/>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    <div className="flex flex-wrap gap-2">
      <button type="button" className="btn-primary" disabled={busy || disabled || !message.trim()} onClick={() => generate()}>
        <Send size={15} className="mr-2"/>{busy ? 'Writing…' : history.length ? 'Ask assistant' : 'Generate draft'}</button>
      {campaign.htmlBody && ['Make the current email shorter.', 'Make the current email friendlier.', 'Translate the current email to Filipino.'].map(text =>
        <button key={text} type="button" disabled={busy || disabled} onClick={() => generate(text)} className="rounded-lg border px-3 py-2 text-xs disabled:opacity-50">{text.replace('the current email ', '').replace('.', '')}</button>)}
    </div>
  </section>;
}
